import TerrainGpsMap from './TerrainGpsMap';
import {ResilientMapTiles} from '../ResilientMapTiles';
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import {
  DebouncedInvalidateMapSize,
  FitBoundsOnce,
  MapScrollWheelGuard,
} from '../gpsMapUtils';

// @ts-expect-error leaflet bundler
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function startIcon() {
  return L.divIcon({
    className: '',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    html: '<div class="wm-map-marker wm-map-marker--start">S</div>',
  });
}

function endIcon() {
  return L.divIcon({
    className: '',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    html: '<div class="wm-map-marker wm-map-marker--finish">F</div>',
  });
}

function hoverIcon() {
  return L.divIcon({
    className: '',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    html: '<div class="wm-map-marker wm-map-marker--hover">H</div>',
  });
}

/** Fly/zoom map when user selects a lap or peak segment. */
function FlyToRoute({ positions, token }: { positions?: [number, number][]; token: number }) {
  const map = useMap();
  const lastToken = useRef(0);
  useEffect(() => {
    if (!token || token === lastToken.current || !positions || positions.length < 2) return;
    lastToken.current = token;
    map.fitBounds(L.latLngBounds(positions), { padding: [48, 48], maxZoom: 17, animate: true });
  }, [map, positions, token]);
  return null;
}


export type MapTileStyle = 'street' | 'light' | 'outdoor' | 'dark';

function LeafletGpsMap({
  gpsRoute,
  gpsSelection,
  hoverPosition,
  mapStyle = 'outdoor',
  className,
  fitKey,
  focusToken = 0,
}: {
  gpsRoute: [number, number][];
  gpsSelection?: [number, number][];
  hoverPosition?: [number, number] | null;
  mapStyle?: MapTileStyle;
  className?: string;
  /** Changes when route source changes — initial fit runs once per key. */
  fitKey?: string;
  /** Increment to fly/zoom to `gpsSelection` after lap/peak tap. */
  focusToken?: number;
}) {
  const { t } = useTranslation();


  if (gpsRoute.length < 2) {
    return (
      <div className={`wm-map-empty ${className ?? ''}`}>
        <span>{t('workoutDetail.map.noGps')}</span>
      </div>
    );
  }

  const center = gpsRoute[Math.floor(gpsRoute.length / 2)];
  const hasSelection = !!(gpsSelection && gpsSelection.length >= 2);

  return (
    <div className={`wm-map-wrap ltr-island ${className ?? ''}`}>
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom
        touchZoom
        doubleClickZoom
        dragging
        zoomSnap={0.5}
        style={{ width: '100%', height: '100%' }}
      >
        <ResilientMapTiles mapStyle={mapStyle} />
        <CompactMapCredits />
        <MapScrollWheelGuard />
        <DebouncedInvalidateMapSize />
        <FitBoundsOnce positions={gpsRoute} fitKey={fitKey ?? 'default'} />
        <FlyToRoute positions={gpsSelection} token={focusToken} />

        <Polyline
          positions={gpsRoute}
          pathOptions={{
            color: hasSelection ? 'rgba(74,144,217,0.35)' : '#4A90D9',
            weight: hasSelection ? 2.5 : 3.5,
            opacity: 0.9,
            lineCap: 'round',
            lineJoin: 'round',
          }}
        />

        {hasSelection && (
          <Polyline
            positions={gpsSelection!}
            pathOptions={{
              color: '#5B9BD5',
              weight: 5,
              opacity: 0.95,
              lineCap: 'round',
              lineJoin: 'round',
            }}
          />
        )}

        <Marker position={gpsRoute[0]} icon={startIcon()} />
        <Marker position={gpsRoute[gpsRoute.length - 1]} icon={endIcon()} />
        {hoverPosition ? <Marker position={hoverPosition} icon={hoverIcon()} /> : null}
      </MapContainer>
    </div>
  );
}

export type GpsMapProps = React.ComponentProps<typeof LeafletGpsMap>;

export function GpsMap(props: GpsMapProps) {
  const [unavailable, setUnavailable] = useState(false);
  return unavailable || props.gpsRoute.length < 2
    ? <LeafletGpsMap {...props} />
    : <TerrainGpsMap {...props} onUnavailable={() => setUnavailable(true)} />;
}

function CompactMapCredits() {
  const map = useMap();
  const { t } = useTranslation();
  useEffect(() => {
    const control = map.attributionControl;
    control.setPrefix(false);
    const update = () => {
      const container = control.getContainer();
      if (!container || container.querySelector('details')) return;
      const details = document.createElement('details');
      details.className = 'wm-map-credits';
      const summary = document.createElement('summary');
      summary.textContent = 'ⓘ';
      summary.setAttribute('aria-label', t('workoutDetail.map.mapCredits'));
      details.appendChild(summary);
      while (container.firstChild) details.appendChild(container.firstChild);
      container.appendChild(details);
    };
    update();
    const container = control.getContainer();
    const observer = new MutationObserver(update);
    if (container) observer.observe(container, { childList: true });
    return () => observer.disconnect();
  }, [map, t]);
  return null;
}
